import { vizColor_hoverOpacity } from '@opengov/capital-style';
import { IChartCalculations, IPieDataWithMetaData } from '@opengov/components-visualization';
import {
  coordinateInRect,
  ICategorizedPoint,
  ICoordinate,
  IGraphElement,
  IRectangle,
  rectsDifferent
} from '@opengov/data-viz';
import { elementSome } from '@opengov/embed-tiles/src/tiles/data';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { OG_TARGET_CATEGORY } from '@opengov/reporting-transforms/visualizations';
import {
  IColorVariants,
  ILineChartCalculations,
  IPieChartCalculations,
  IStackedBarChartCalculations
} from '@opengov/reporting-transforms/visualizations/types';
import { EReportChartType, IReportConfiguration, OG_META_DATA_KEY } from '@opengov/reporting-types';
import {
  createColumnFilter,
  FilterOperators,
  IEqualFilter,
  IIsBlankFilter
} from '@opengov/reporting-types/src/reportFilters';
import Color from 'color';
import { startCase } from 'lodash';

import { IReportTooltip } from '../../ReportTooltip';
import { reportSavedViewEditActionCreators } from '../../../../module/state';
import { IReportLocale } from '../../../common/locale';
import { IReportVisualizationProps } from '../ReportVisualization';
import { chartActionCreators, ITooltipBase } from '../state';

import {
  findAmountFormatter,
  formatChangeValue,
  hasPreviousData,
  IGraphCallbackCategoryData,
  ILineGraphCallbackData,
  positionTooltipBottomCenter
} from './selectReportVisualizationPropsUtils';
import { monitor } from '../../../performance';

// Translate highlight color (with opacity) into a plain hex value for layering over darker colors.
// Attempting to move this to customHighlightColorFor in customColorUtils caused unpredictable highlight
// colors for Pie charts and Line charts.
const translateHighlightColorToHex = (color: string): string => {
  const highlightColor = Color(color);
  const opacity = 1 - vizColor_hoverOpacity;

  return Color.rgb(
    Math.round(opacity * 255 + opacity * highlightColor.red()),
    Math.round(opacity * 255 + opacity * highlightColor.green()),
    Math.round(opacity * 255 + opacity * highlightColor.blue())
  ).hex();
};

const startMeasurementAfterUpdatingChartCategory = () => {
  const selectChartCategoryMeasurementObject = {
    measureName: 'charts-details-update-chart-category-load',
    spinnerWaitOptions: {
      shouldDetect: true,
      selector: '[data-test="data-table-updating-indicator"]'
    },
    loadingBlocksWaitOptions: { shouldDetect: false }
  };
  void monitor.measureUntilDoneLoading(selectChartCategoryMeasurementObject);
};

export const selectReportVisualizationPropsHandlers = {
  updateSelectedChartCategory:
    (rcActions, rcEffects) =>
    (columnName, category, activeViewTab, colorMap, isNone, numberOfCategories): void => {
      if (activeViewTab === 1) {
        // Selects a category to show up in the color picker if on the presentation tab.
        // activeViewTab = 1 is the presentation tab.
        reportSavedViewEditActionCreators.setActiveCategory({
          name: category,
          color: colorMap && colorMap[category].base
        });
        return;
      }

      if (isNone || numberOfCategories <= 1) {
        return;
      }

      const filter: IEqualFilter | IIsBlankFilter =
        // Use isBlankFilter if we are filtering by Empty Values
        category !== ''
          ? {
              operator: FilterOperators.Equal,
              columnName,
              value: category
            }
          : {
              operator: FilterOperators.Blank,
              columnName,
              value: null
            };
      rcActions.setColumnFilters([createColumnFilter(filter)]);
      rcEffects.createReportConfigurationAndUpdateUrl();
      startMeasurementAfterUpdatingChartCategory();
    },
  updateSelectedCategory:
    (actions: typeof chartActionCreators) =>
    (calcs: ILineChartCalculations | IStackedBarChartCalculations | IPieChartCalculations) =>
    (category: string): void => {
      // this is stackbar specific
      const data = calcs.data;
      const categoricalElements = data.reduce((acc, d) => {
        if (d[category]) {
          acc.push({ id: `${d.id}`, category, value: null });
        }
        return acc;
      }, []);
      actions.setSelectedElements(categoricalElements as IGraphElement[]);
      actions.setSelectedCategory(category);
    },
  elementColorFor:
    (selectedCategory: string, selectedElements: IGraphElement[], colorMap: { [key: string]: IColorVariants }) =>
    (element: IGraphElement): string => {
      const colors = colorMap[element.category];
      if (!colors) {
        return;
      }

      if (
        (selectedElements.length === 0 && selectedCategory) ||
        (selectedElements.length && !selectedElements.some(elementSome(element)))
      ) {
        return colors.highlight;
      }
      return colors.base;
    },
  elementColorForPie:
    (selectedCategory: string, colorMap: { [key: string]: IColorVariants }, excludedCategories) =>
    (category: string): string => {
      const colors = colorMap[category];
      const baseColor = colors && colors.base;
      const highlightColor = colors && colors.highlight;
      if (excludedCategories && excludedCategories.includes(category)) {
        return 'none';
      }
      if (selectedCategory) {
        return category === selectedCategory ? baseColor : highlightColor;
      }
      return baseColor;
    },
  elementColorForLine:
    (colorMap) =>
    (category: string): string => {
      const colors = colorMap[category];
      return colors && colors.base;
    },
  elementColorForInnerPoint:
    (colorMap, selectedPoint) =>
    (point: ICategorizedPoint): string => {
      const colors = colorMap[point.category];
      const baseColor = colors && colors.base;

      if (selectedPoint && JSON.stringify(point) === JSON.stringify(selectedPoint)) {
        return baseColor;
      }

      return translateHighlightColorToHex(colors && colors.highlight);
    },
  elementColorForInnerPointTarget:
    (colorMap, selectedPoint, selectedElements) =>
    (point: ICategorizedPoint): string => {
      const category = point.data[OG_META_DATA_KEY].targetStatus;
      const colors = colorMap[category];
      const baseColor = colors && colors.base;
      let selectedElementsHasPoint = false;

      if (selectedPoint && JSON.stringify(point) === JSON.stringify(selectedPoint)) {
        return baseColor;
      }

      selectedElements.find((element) => {
        if (element.category === category) {
          selectedElementsHasPoint = true;
        }
      });

      if (selectedElementsHasPoint) {
        return baseColor;
      }

      return translateHighlightColorToHex(colors && colors.highlight);
    },
  elementColorForOuterPoint:
    (colorMap, selectedPoint) =>
    (point: ICategorizedPoint): string => {
      const colors = colorMap[point.category];
      if (selectedPoint && JSON.stringify(point) === JSON.stringify(selectedPoint)) {
        return 'white';
      }
      return colors && colors.base;
    },
  elementColorForOuterPointTarget:
    (colorMap, selectedPoint, selectedElements) =>
    (point: ICategorizedPoint): string => {
      const category = point.data[OG_META_DATA_KEY].targetStatus;
      const colors = colorMap[category];
      let selectedElementsHasPoint = false;

      if (selectedPoint && JSON.stringify(point) === JSON.stringify(selectedPoint)) {
        return 'white';
      }

      selectedElements.find((element) => {
        if (element.category === category) {
          selectedElementsHasPoint = true;
        }
      });

      if (selectedElementsHasPoint) {
        return 'white';
      }

      return colors && colors.base;
    },
  onElementEnterStack:
    (actions: typeof chartActionCreators) =>
    (
      allData,
      tooltip: ITooltipBase,
      colorMap: { [key: string]: IColorVariants },
      tickLabelFormatter: (domain: string | number) => string,
      percentFormatter: (p: { value: number }) => string,
      columnFormatter: (columnType) => (p: { value: number }) => string,
      config: IReportConfiguration,
      emptyValuesText: IReportLocale['reportExplorer']['emptyValues'],
      notApplicableText: IReportLocale['reportExplorer']['notApplicable']
    ): IReportVisualizationProps['onElementEnter'] => {
      const categorizedByType = config.visualizationQuery.categorizedByColumn.type;
      const valueFormatter = columnFormatter(config.visualizationQuery.showColumn.type);
      const amountFormatter = findAmountFormatter(config, columnFormatter);
      const previousPointTest = hasPreviousData(allData);

      return (category: string, data: IGraphCallbackCategoryData, domCoordinate: ICoordinate, target?: any) => {
        const meta = data[OG_META_DATA_KEY];
        const value = valueFormatter({ value: data[category] });

        const change = previousPointTest(data.id, category)
          ? formatChangeValue(meta, category, percentFormatter, notApplicableText)
          : null;

        const percentOfTotal =
          meta.percentTotal && meta.percentTotal[category] && meta.percentTotal[category] !== '1.0'
            ? percentFormatter({ value: parseFloat(meta.percentTotal[category]) })
            : null;
        const total = meta.total && meta.total[category] ? amountFormatter({ value: meta.total[category] }) : null;
        const amount = meta.value && meta.value[category] ? amountFormatter({ value: meta.value[category] }) : null;
        let categoryLabel = category === '' ? emptyValuesText : category;
        const colorsForCategory = colorMap[category];
        const targetStatus = meta.targetStatus;

        if (categorizedByType === BasicColumnTypes.Boolean) {
          categoryLabel = startCase(categoryLabel);
        }

        actions.setTooltip({
          ...tooltip,
          data: {
            amount,
            category: categoryLabel,
            categoryColor: colorsForCategory && colorsForCategory.base,
            change,
            percentOfTotal,
            targetStatus,
            total,
            xAxis: tickLabelFormatter(data.name),
            value
          },
          show: 'measure',
          targetElementRect: target,
          targetSvgElement: null
        });

        actions.setSelectedCategory(category);
        actions.setSelectedElements([{ category, id: data.id, value: null }]);
      };
    },
  onElementEnterLine:
    (actions: typeof chartActionCreators) =>
    (
      allData,
      tooltip: ITooltipBase,
      colorMap: { [key: string]: IColorVariants },
      tickLabelFormatter: (domain: string | number) => string,
      percentFormatter: (p: { value: number }) => string,
      columnFormatter: (columnType) => (p: { value: number }) => string,
      config: IReportConfiguration,
      emptyValuesText: IReportLocale['reportExplorer']['emptyValues'],
      notApplicableText: IReportLocale['reportExplorer']['notApplicable']
    ): IReportVisualizationProps['onElementEnter'] => {
      const categorizedByType = config.visualizationQuery.categorizedByColumn.type;
      const valueFormatter = columnFormatter(config.visualizationQuery.showColumn.type);
      const amountFormatter = findAmountFormatter(config, columnFormatter);
      const previousPointTest = hasPreviousData(allData);

      return (category: string, data: ILineGraphCallbackData, domCoordinate: ICoordinate, target?: any) => {
        const meta = data.data[OG_META_DATA_KEY];
        const categoryOrTargetStatus = meta.targetStatus ? meta.targetStatus : category;
        const colorsForCategory = colorMap[categoryOrTargetStatus];
        const value = valueFormatter({ value: data.value });
        const change = previousPointTest(data.id, category)
          ? formatChangeValue(meta, category, percentFormatter, notApplicableText)
          : null;
        const amount = meta.value && meta.value[category] ? amountFormatter({ value: meta.value[category] }) : null;
        let categoryLabel = category === '' ? emptyValuesText : category;

        if (category === OG_TARGET_CATEGORY) {
          categoryLabel = meta.targetStatus;
        }

        if (categorizedByType === BasicColumnTypes.Boolean) {
          categoryLabel = startCase(categoryLabel);
        }

        actions.setTooltip({
          ...tooltip,
          data: {
            amount,
            category: categoryLabel,
            categoryColor: colorsForCategory && colorsForCategory.base,
            change,
            value,
            xAxis: tickLabelFormatter(data.data.name)
          },
          show: 'measure',
          targetElementRect: target.getBoundingClientRect(),
          targetSvgElement: target
        });
        actions.setSelectedCategory(category);
      };
    },
  onElementEnterPie:
    (actions: typeof chartActionCreators) =>
    (
      tooltip: ITooltipBase,
      colorMap: { [key: string]: IColorVariants },
      showAsPercentOfTotal: IChartCalculations['showAsPercentOfTotal'],
      tickLabelFormatter: (domain: string | number) => string,
      percentFormatter: (p: { value: number }) => string,
      columnFormatter: (columnType) => (p: { value: number }) => string,
      config: IReportConfiguration,
      excludedCategories: string[],
      emptyValuesText: IReportLocale['reportExplorer']['emptyValues']
    ): IReportVisualizationProps['onElementEnter'] => {
      const categorizedByType = config.visualizationQuery.categorizedByColumn.type;
      const valueFormatter = columnFormatter(config.visualizationQuery.showColumn.type);
      const amountFormatter = findAmountFormatter(config, columnFormatter);

      return (category: string, data: IPieDataWithMetaData, domCoordinate: ICoordinate, target?: any) => {
        const colorsForCategory = colorMap[category];
        const meta = data.meta;
        // value is what the slice is made from. It can be a number or a %
        const value = valueFormatter({ value: meta.value });
        const total = meta.total ? amountFormatter({ value: meta.total }) : null;
        // amount is the actual value even if showing by percent
        const amount = meta.amount && showAsPercentOfTotal ? amountFormatter({ value: meta.amount }) : null;

        let categoryLabel = category === '' ? emptyValuesText : category;
        const percentOfTotal =
          !showAsPercentOfTotal && meta.percentTotal && meta.percentTotal !== 1
            ? percentFormatter({ value: meta.percentTotal })
            : null;

        if (excludedCategories && excludedCategories.includes(category)) {
          return;
        }

        if (categorizedByType === BasicColumnTypes.Boolean) {
          categoryLabel = startCase(categoryLabel);
        }

        actions.setTooltip({
          ...tooltip,
          domCoordinate,
          data: {
            amount,
            category: categoryLabel,
            categoryColor: colorsForCategory && colorsForCategory.base,
            percentOfTotal,
            total,
            xAxis: meta.range.map((d) => tickLabelFormatter(d.name)).join(' - '),
            value
          },
          show: 'measure',
          targetSvgElement: target
        });
        actions.setSelectedCategory(category);
      };
    },
  onElementLeave:
    (actions: typeof chartActionCreators) =>
    (tooltip: ITooltipBase) =>
    (coordinate: ICoordinate): void => {
      const tooltipRect = tooltip.boundingClientRect;

      if (!tooltipRect || !coordinateInRect(coordinate, tooltipRect)) {
        actions.setTooltip({ ...tooltip, show: 'hide' });
        actions.setSelectedElements([]);
        actions.setSelectedCategory(null);
      }
    },
  onMouseLeaveTooltip: (actions: typeof chartActionCreators) => (tooltip: ITooltipBase) => (): void => {
    actions.setTooltip({ ...tooltip, show: 'hide' });
    actions.setSelectedElements([]);
    actions.setSelectedCategory(null);
  },
  reportTooltipBoundingClientRect:
    (actions: typeof chartActionCreators) =>
    (chartType: EReportChartType, tooltip: ITooltipBase, containerRect: IRectangle) =>
    (rect: IRectangle, show: IReportTooltip['show']): void => {
      if (show === 'measure' || (show === 'show' && rectsDifferent(rect, tooltip.boundingClientRect))) {
        const targetRect = tooltip.targetElementRect;
        const offset = chartType === EReportChartType.Line ? Number(tooltip.targetSvgElement.getAttribute(`r`)) / 2 : 0;
        if (chartType === EReportChartType.Pie) {
          const { domCoordinate } = tooltip;
          const coord = positionTooltipBottomCenter(
            domCoordinate.x,
            domCoordinate.y,
            containerRect,
            tooltip.boundingClientRect
          );
          actions.setTooltip({ ...tooltip, show: 'show', coordinate: coord, boundingClientRect: rect });
          return;
        }

        const coordinate = positionTooltipBottomCenter(
          targetRect.left + targetRect.width / 2,
          targetRect.top + offset,
          containerRect,
          rect
        );
        coordinate.y = Math.ceil(coordinate.y);
        actions.setTooltip({ ...tooltip, show: 'show', coordinate, boundingClientRect: rect });
      }
    }
};
